import _ from 'lodash';

import { captureException } from '@playbookux/playbook-web-core';
import {
  TREE_TEST_ERROR,
  TREE_TEST_NEXT_QUESTION,
  TREE_TEST_PROJECT_VALUES,
  TREE_TEST_QUESTIONS_ANSWERS,
  TREE_TEST_QUESTIONS_ORDER,
  TREE_TEST_RESET_QUESTIONS,
  TREE_TEST_RESET_SCREENER_ANSWERS,
  TREE_TEST_TEST_SESSION,
} from './action-types';

import { TYPE_QUESTIONS } from '../components/questions-components';
import { orderArray } from '../utils/order-array';
import { saveTestSessionAnswers } from '../graphql/tree-test';

import omitDeep from '../utils/omit-deep';

/**
 * Set initial questions values.
 * @param {array} questions The array with all the questions
 * @param {string} fieldOrder The field used to order the questions
 * @param {boolean} randomize Used to know if the array needs to be randomized or not.
 */
export const setQuestions = (questions, fieldOrder, randomize) => async (dispatch) => {
  const orderedQuestions = orderArray(questions, fieldOrder, randomize);

  const flatArrayQuestions = [];
  _.forEach(orderedQuestions, (question) => {
    flatArrayQuestions.push({ ...question, type: 'task' });

    const { followUpQuestions } = question;

    // If the there are follow up questions these ones are added on array after the Question.
    if (!_.isEmpty(followUpQuestions)) {
      const orderedFollowUpQuestions = orderArray(followUpQuestions, 'questionOrder', false);

      _.forEach(orderedFollowUpQuestions, (followUpQuestion) => {
        flatArrayQuestions.push({
          ...followUpQuestion,
          type: 'followUpQuestion',
          questionTaskId: question.id,
        });
      });
    }
  });

  dispatch({
    type: TREE_TEST_QUESTIONS_ORDER,
    payload: {
      currentQuestion: _.head(flatArrayQuestions),
      maxTasks: orderedQuestions.length,
      questions: flatArrayQuestions,
    },
  });
};

export const setNextQuestion = () => async (dispatch, getState) => {
  const { treeTest: state } = getState();
  const { questions, maxTasks } = state;

  let {
    currentQuestion,
    isLastQuestion,
    questionIndex,
  } = state;

  const questionPosition = _.findIndex(questions, (question) => (
    question.id === currentQuestion.id
  ));

  if (questionPosition + 1 < questions.length) {
    currentQuestion = questions[questionPosition + 1];
  }

  if (questionIndex < maxTasks) {
    questionIndex = currentQuestion.type === 'task' ? questionIndex + 1 : questionIndex;
  }

  if (questionPosition + 2 === questions.length) {
    isLastQuestion = true;
  }

  dispatch({
    type: TREE_TEST_NEXT_QUESTION,
    payload: {
      currentQuestion,
      isLastQuestion,
      questionIndex,
    },
  });
};

export const clearQuestions = () => async (dispatch) => {
  dispatch({
    type: TREE_TEST_RESET_QUESTIONS,
  });
};

export const setAnswerValue = (value, state) => {
  const { currentQuestion, testSession } = state;

  const answer = {
    questionId: currentQuestion.id,
    testSessionId: testSession.id,
    valueText: '',
    videoEndTime: 0,
    videoStartTime: 0,
    value,
  };

  switch (currentQuestion.questionType) {
    case TYPE_QUESTIONS.WRITTEN: {
      answer.valueText = value;
      break;
    }
    case TYPE_QUESTIONS.RANGE: {
      answer.questionOptionId = value;
      break;
    }
    case TYPE_QUESTIONS.SINGLE_SELECT: {
      const singleAnswered = _.head(_.keys(value));
      answer.questionOptionId = singleAnswered;
      break;
    }
    case TYPE_QUESTIONS.MULTIPLE_SELECT: {
      const multipleAnswered = _.keys(value);
      answer.questionOptionIds = multipleAnswered;
      break;
    }
    default: break;
  }

  return answer;
};

/**
 * Set some project values to be used in the questions.
 */
export const setProjectValues = (project) => async (dispatch) => {
  dispatch({
    type: TREE_TEST_PROJECT_VALUES,
    payload: { project },
  });
};

export const setTestSession = (testSession, isPreview = false) => async (dispatch) => {
  dispatch({
    type: TREE_TEST_TEST_SESSION,
    payload: { testSession, isPreview },
  });
};

/**
 * Clear screener answers, remove current question and all screener questions from redux.
 */
export const clearScreenerAnswers = () => (dispatch) => {
  dispatch({
    type: TREE_TEST_RESET_SCREENER_ANSWERS,
  });
};

export const setError = (error) => (dispatch) => {
  dispatch({
    type: TREE_TEST_ERROR,
    payload: { error },
  });
};

export const setAnswers = (newAnswer) => async (dispatch, getState) => {
  const { treeTest: state } = getState();

  const {
    answers,
    currentQuestion,
    isLastQuestion,
    testSession,
    isPreview,
  } = state;

  const isTreeTestQuestion = currentQuestion.questionType === 'tree_test';
  let sessionCompleted = false;
  let answer = {};

  if (isTreeTestQuestion) {
    answer = {
      questionId: currentQuestion.id,
      testSessionId: testSession.id,
      treeTestPaths: newAnswer,
      valueText: '',
      videoEndTime: 0,
      videoStartTime: 0,
    };
  } else {
    answer = setAnswerValue(newAnswer, state);
  }

  if (!isLastQuestion) {
    // Update redux state and go to the next question
    dispatch({
      type: TREE_TEST_QUESTIONS_ANSWERS,
      payload: { answer },
    });

    dispatch(setNextQuestion());

    sessionCompleted = false;
  } else {
    // Save answers on database if it is on the last question
    try {
      if (!isPreview) {
        const attributes = _.map([...answers, answer], (testSessionAnswer) => {
          const updatedAnswer = omitDeep(testSessionAnswer, 'value');

          return { ...updatedAnswer };
        });

        await saveTestSessionAnswers({ attributes });
      }

      sessionCompleted = true;
      dispatch(clearQuestions());
    } catch (err) {
      captureException('SET_ANSWERS_FAILED', err);
      setError({ message: err });
      sessionCompleted = false;
    }
  }

  return { sessionCompleted };
};
